import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Mail, Heart } from "lucide-react";

const STORAGE_KEY_DISMISSED = "newsletter-popup-dismissed";
const STORAGE_KEY_SUBSCRIBED = "newsletter-popup-subscribed";
const DISMISS_DAYS = 7;
const TRIGGER_DELAY_MS = 30_000;
const TRIGGER_SCROLL_PCT = 0.5;

// Pages where popup should NOT show
const EXCLUDED_PREFIXES = [
  "/admin",
  "/daddy",
  "/coming-soon",
  "/privacy",
  "/terms",
  "/cookie-policy",
  "/dmca",
  "/accessibility",
  "/data-request",
  // Recipe detail pages already have a sticky ad + jump-to-recipe button;
  // stacking a popup on top hurts readability on mobile.
  "/recipes/",
];

function shouldShowPopup(pathname: string): boolean {
  if (localStorage.getItem(STORAGE_KEY_SUBSCRIBED)) return false;

  const dismissed = localStorage.getItem(STORAGE_KEY_DISMISSED);
  if (dismissed) {
    const dismissedAt = Number(dismissed);
    if (Date.now() - dismissedAt < DISMISS_DAYS * 86_400_000) return false;
  }

  if (EXCLUDED_PREFIXES.some((p) => pathname.startsWith(p))) return false;

  return true;
}

const NewsletterPopup = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const triggered = useRef(false);
  const openedAt = useRef(Date.now());

  const triggerPopup = useCallback(() => {
    if (triggered.current) return;
    if (!shouldShowPopup(location.pathname)) return;
    triggered.current = true;
    setOpen(true);
  }, [location.pathname]);

  useEffect(() => {
    if (!shouldShowPopup(location.pathname)) return;

    // Timer trigger
    const timer = setTimeout(triggerPopup, TRIGGER_DELAY_MS);

    // Scroll trigger
    const onScroll = () => {
      if (triggered.current) return;
      const scrollPct =
        window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      if (scrollPct >= TRIGGER_SCROLL_PCT) triggerPopup();
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // Exit-intent trigger (desktop only — fires when mouse leaves viewport toward top)
    const onMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) triggerPopup();
    };
    document.addEventListener("mouseleave", onMouseLeave);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [location.pathname, triggerPopup]);

  const handleDismiss = (isOpen: boolean) => {
    if (!isOpen && status !== "success") {
      localStorage.setItem(STORAGE_KEY_DISMISSED, String(Date.now()));
    }
    setOpen(isOpen);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Hidden field alone isn't proof of a bot — browsers autofill it.
    if (honeypot && Date.now() - openedAt.current < 2500) return;


    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName || trimmedName.length > 100) {
      setErrorMsg("Please enter your name (max 100 characters).");
      setStatus("error");
      return;
    }
    if (!trimmedEmail || trimmedEmail.length > 255 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setErrorMsg("Please enter a valid email address.");
      setStatus("error");
      return;
    }

    setStatus("loading");
    setErrorMsg("");

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15_000);

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscribe-newsletter`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ name: trimmedName, email: trimmedEmail, source: "popup" }),
          signal: controller.signal,
        }
      );
      clearTimeout(timeout);

      if (res.status === 429) {
        setErrorMsg("That went through a moment ago — give it a minute and check your inbox.");
        setStatus("error");
        return;
      }
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        const message = typeof payload?.error === "string" ? payload.error : "";
        // Already-on-the-list is a success from the reader's point of view.
        if (/duplicate|already/i.test(message)) {
          localStorage.setItem(STORAGE_KEY_SUBSCRIBED, "1");
          setStatus("success");
          return;
        }
        setErrorMsg(message || "We couldn't sign you up just now. Please try again in a moment.");
        setStatus("error");
        return;
      }

      localStorage.setItem(STORAGE_KEY_SUBSCRIBED, "1");
      setStatus("success");
    } catch {
      setErrorMsg("Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDismiss}>
      <DialogContent
        className="bg-[hsl(var(--wood))] border-[3px] border-gold text-cream max-w-lg rounded-2xl p-0 overflow-hidden gap-0"
        // Don't steal focus on open, and don't yank focus back when the mobile
        // keyboard resizes the viewport or an ad script grabs focus — that was
        // dismissing the on-screen keyboard mid-typing.
        onOpenAutoFocus={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (status === "loading") e.preventDefault();
        }}
      >
        {/* Gold accent bar */}
        <div className="h-2 bg-gold w-full" />

        <div className="px-7 pb-8 pt-6 min-w-0 w-full">
          <DialogHeader className="text-center items-center space-y-1 mb-1 min-w-0">
            <DialogTitle className="font-heading text-2xl md:text-3xl uppercase tracking-wide text-gold flex flex-wrap items-center justify-center gap-2 pr-6">
              <Mail className="h-6 w-6" />
              Never Miss a Recipe!
            </DialogTitle>
            <DialogDescription className="font-handwritten text-lg md:text-xl leading-snug text-cream/90 text-center w-full max-w-full break-words">
              Subscribe & get my free Cowboy Spice Mix recipe 🤠
            </DialogDescription>
          </DialogHeader>

          {status === "success" ? (
            <div className="text-center py-8 space-y-3">
              <Heart className="h-10 w-10 text-gold mx-auto animate-wiggle" />
              <p className="font-heading text-2xl uppercase text-gold">
                You're on the list!
              </p>
              <p className="font-handwritten text-2xl text-cream/80">
                Check your inbox for the Cowboy Spice Mix recipe 🤠
              </p>
              <Button
                onClick={() => setOpen(false)}
                className="mt-3 bg-gold text-foreground font-heading uppercase text-base tracking-wide hover:bg-gold-dark h-12 px-8"
              >
                Keep Browsin'
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-5 space-y-5">
              {/* Honeypot — hidden from real users */}
              <div className="absolute opacity-0 pointer-events-none" aria-hidden="true">
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="popup-name" className="text-cream/80 font-heading uppercase text-sm tracking-wider">
                  First Name
                </Label>
                <Input
                  id="popup-name"
                  placeholder="Your first name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={100}
                  className="bg-cream/10 border-cream/20 text-cream placeholder:text-cream/40 focus-visible:ring-gold h-12 text-base rounded-lg"
                  disabled={status === "loading"}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="popup-email" className="text-cream/80 font-heading uppercase text-sm tracking-wider">
                  Email
                </Label>
                <Input
                  id="popup-email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  maxLength={255}
                  className="bg-cream/10 border-cream/20 text-cream placeholder:text-cream/40 focus-visible:ring-gold h-12 text-base rounded-lg"
                  disabled={status === "loading"}
                />
              </div>

              {status === "error" && errorMsg && (
                <p className="text-destructive text-sm font-body">{errorMsg}</p>
              )}

              <Button
                type="submit"
                disabled={status === "loading"}
                className="w-full bg-gold text-foreground font-heading uppercase text-lg tracking-wide hover:bg-gold-dark disabled:opacity-60 h-14 rounded-lg"
              >
                {status === "loading" ? "Subscribing…" : "Yes, I Want Recipes!"}
              </Button>

              <p className="text-center text-base text-cream/50 font-handwritten">
                No spam, ever. Unsubscribe anytime.
              </p>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewsletterPopup;
